export const MIN_STATS_WATCHER_STEP = 100; // Minimum duration between stats computation, in ms.
export const DEFAULT_STATS_WATCHER_STEP = 1000; // Default duration between stats computation, in ms.

/**
 * Create a statistics watcher that can show statistics about a specified peer connection in the console or pass them to a callback.
 * @param {RTCPeerConnection} peerConnection
 * @param {MediaStream} [stream] If provided, the stats watching will immediately start upon watcher creation.
 * @param {{warn: function, debug: function, log: function, error: function, info: function}} [logger] - The logger to be used.
 * @param {function({stats})} [onStats] The callback for every interval at which data has been collected.
 * An object containing the statistics is passed to it. Note that the first callback call will be done with some missing statistics, due to internal computation.
 * @param {Number} [interval] The interval at which the statistics will callback in milliseconds. Defaults to 1 second in order to simplify calculations, with a minimum of 100ms.
 * @returns {{stop: *, start: *, instant: *}}
 */
export const createStatsWatcher = ({
  peerConnection,
  stream,
  logger,
  onStats,
  interval = DEFAULT_STATS_WATCHER_STEP,
}) => {
  if (!(peerConnection instanceof RTCPeerConnection)) {
    throw new Error('StatsWatcher: Expected RTCPeerConnection instance as unique argument');
  }
  let intervalId = null;
  let storedStats = {};
  let stepCount = 0;

  /**
   * Start the statistics watcher.
   *
   * @param {MediaStream} stream The stream to which are attached the tracks that will be watched for statistics extraction.
   * @param {function({stats})} [callback] The callback for every interval at which data has been collected.
   * An object containing the statistics is passed to it. Note that the first callback call will be done with some missing statistics, due to internal computation.
   * @param {Number} [interval=1000] The interval at which the statistics will callback in milliseconds. Defaults to 1 second in order to simplify calculations, with a minimum of 100ms.
   * @param {Boolean} [log=true] Whether to log the output in the // console.
   */
  const start = (stream, { onStats = () => {}, interval = 1000, log = true } = {}) => {
    if (intervalId != null) {
      logger.log('Stats: Stats watcher already started.');
      return null;
    }

    if (interval < MIN_STATS_WATCHER_STEP) {
      logger.log(`Stats: Asked for interval of ${interval} which is too short. Interval reset to ${MIN_STATS_WATCHER_STEP}.`);
      interval = MIN_STATS_WATCHER_STEP;
    }
    const frequency = 1000 / interval;

    intervalId = setInterval(async() => {
      let instantStats;
      try {
        instantStats = await instant(stream, { log: false });
      } catch (err) {
        if (err.message === 'no_track_bearer_for_track_id') {
          return stop();
        }
        throw err;
      }
      const stats = getComputedStats(storedStats, instantStats, frequency, stepCount);
      stepCount++;
      storedStats = { ...stats };
      logger.log(stats, stepCount);
      onStats(stats);
    }, interval);
  };

  /**
   * Stop the statistics watcher.
   */
  const stop = () => {
    try {
      clearInterval(intervalId);
      intervalId = null;
    } catch (err) {
      logger.log('Tried to stop the stats watcher, but it wasn\'t running.');
    }
  };

  /**
   * Fetch a snapshot of the statistics about a peer connection.
   *
   * @param {Object} stream The id of the stream from which the statistics will be collected. If not specified, statistics on the local outgoing stream will be collected instead.
   * @param {Boolean} [log=true] Whether to log the output in the console.
   * @returns {Promise<{videoStats: {}, audioStats: {}, advancedStats: {}}>} A promise resolving with a statistics object.
   */
  const instant = async(stream, { log = true } = {}) => {
    const videoBearer = findTrackBearerInTransceivers(stream.getVideoTracks()[0]?.id);
    // const audioBearer = findTrackBearerInTransceivers(stream.getAudioTracks()[0]?.id);

    const videoReport = await videoBearer.getStats();
    const videoStats = extractVideoStatsFromReport(videoReport);
    // const audioReport = await audioBearer.getStats();
    // const audioStats = extractAudioStatsFromReport(audioReport);
    const advancedStats = {
      ...extractAdvancedStatsFromReport(videoReport),
      // ...extractAdvancedStatsFromReport(audioReport),
    };
    const rawStats = {
      videoTrack: extractRawStatsFromReport(videoReport),
      // audioTrack: extractRawStatsFromReport(audioReport)
    };
    return {
      videoStats,
      // audioStats,
      advancedStats,
      rawStats,
    };
  };

  const findTrackBearerInTransceivers = (trackId) => {
    if (typeof trackId !== 'string') {
      throw new Error('invalid_track_id');
    }
    let bearer = null;
    if (peerConnection.getTransceivers().length) {
      peerConnection.getTransceivers().find((transceiver) => {
        if (transceiver.sender?.track?.id === trackId) {
          bearer = transceiver.sender;
          return true;
        }
        if (transceiver.receiver?.track?.id === trackId) {
          bearer = transceiver.receiver;
          return true;
        }
        return false;
      });
    } else {
      peerConnection.getReceivers().find((receiver) => {
        if (receiver.track?.id === trackId) {
          bearer = receiver;
          return true;
        }
        if (receiver.track?.id === trackId) {
          bearer = receiver;
          return true;
        }
        return false;
      });
    }
    if (bearer == null) {
      logger.log(`No track bearer found for ${trackId}`);
      throw new Error('no_track_bearer_for_track_id');
    }
    return bearer;
  };

  const extractRawStatsFromReport = (report) => {
    const stats = [];
    report.forEach(statsSubdict => stats.push(statsSubdict));
    return stats;
  };

  const extractAdvancedStatsFromReport = (report) => {
    const stats = {
      localNetworkType: null,
      localProtocol: null,
      localCandidateType: null,
      localIp: null,
      localPort: null,
      remoteProtocol: null,
      remoteCandidateType: null,
      remoteIp: null,
      remotePort: null,
    };

    let localCandidateId;
    let remoteCandidateId;
    report.forEach(statsSubdict => {
      switch (statsSubdict.type) {
        case 'candidate-pair':
          if (statsSubdict.selected) {
            localCandidateId = statsSubdict.localCandidateId;
            remoteCandidateId = statsSubdict.remoteCandidateId;
          }
          break;
        case 'local-candidate':
          if ((localCandidateId && localCandidateId === statsSubdict.id) || !localCandidateId) {
            stats.localNetworkType = statsSubdict.networkType;
            stats.localProtocol = statsSubdict.protocol;
            stats.localCandidateType = statsSubdict.candidateType;
            stats.localIp = statsSubdict.ip ?? statsSubdict.address;
            stats.localPort = statsSubdict.port;
          }
          break;
        case 'remote-candidate':
          if ((remoteCandidateId && remoteCandidateId === statsSubdict.id) || !remoteCandidateId) {
            stats.remoteProtocol = statsSubdict.protocol;
            stats.remoteCandidateType = statsSubdict.candidateType;
            stats.remoteIp = statsSubdict.ip ?? statsSubdict.address;
            stats.remotePort = statsSubdict.port;
          }
          break;
      }
    });
    return stats;
  };

  const extractVideoStatsFromReport = (report) => {
    const stats = {
      bytesReceived: null,
      bytesSent: null,
      selectedCandidatePairChanges: null,
      framesPerSecond: null,
      framesDecoded: null,
      frameHeight: null,
      frameWidth: null,
      jitter: null,
      qualityLimitationReason: null,
      mimeType: null,
      currentRoundTripTime: null,
      totalRoundTripTime: null,
      availableOutgoingBitrate: null,
      packetLossPercent: null,
    };
    let packetsReceived = 0;
    let packetsLost = 0;
    report.forEach(statsSubdict => {
      switch (statsSubdict.type) {
        case 'candidate-pair':
          stats.currentRoundTripTime = statsSubdict.currentRoundTripTime;
          stats.totalRoundTripTime = statsSubdict.totalRoundTripTime;
          stats.availableOutgoingBitrate = statsSubdict.availableOutgoingBitrate;
          break;
        case 'transport':
          stats.selectedCandidatePairChanges = statsSubdict.selectedCandidatePairChanges;
          packetsReceived = statsSubdict.packetsReceived ?? packetsReceived;
          break;
        case 'track':
          stats.frameHeight = statsSubdict.frameHeight;
          stats.frameWidth = statsSubdict.frameWidth;
          stats.framesPerSecond = statsSubdict.framesPerSecond;
          break;
        case 'outbound-rtp':
          stats.qualityLimitationReason = statsSubdict.qualityLimitationReason;
          stats.bytesSent = statsSubdict.bytesSent;
          break;
        case 'inbound-rtp':
          stats.bytesReceived = statsSubdict.bytesReceived;
          stats.framesDecoded = statsSubdict.framesDecoded;
          stats.framerateMean = statsSubdict.framerateMean;
          stats.jitter = statsSubdict.jitter;
          packetsLost = statsSubdict.packetsLost ?? packetsLost;
          break;
        // For FF
        case 'remote-inbound-rtp': {
          stats.roundTripTime = statsSubdict.roundTripTime;
          packetsLost = statsSubdict.packetsLost ?? packetsLost;
          packetsReceived = statsSubdict.packetsReceived ?? packetsReceived;
          break;
        }
        case 'codec':
          stats.mimeType = statsSubdict.mimeType;
      }
    });
    const roundTripTime = stats.currentRoundTripTime ?? stats.roundTripTime;
    if (roundTripTime != null && !Number.isNaN(roundTripTime)) {
      stats.latencyMs = roundTripTime * 1000;
    }
    if (packetsReceived) {
      stats.packetLossPercent = packetsLost * 100 / packetsReceived;
    }
    return stats;
  };

  // const extractAudioStatsFromReport = (report) => {
  //   const stats = {
  //     bytesReceived: null,
  //     bytesSent: null,
  //     selectedCandidatePairChanges: null,
  //     audioLevel: null,
  //     jitter: null,
  //     mimeType: null,
  //     voiceActivityFlag: null,
  //   };
  //   report.forEach(statsSubdict => {
  //     switch (statsSubdict.type) {
  //       case 'transport':
  //         stats.selectedCandidatePairChanges = statsSubdict.selectedCandidatePairChanges;
  //         break;
  //       case 'track':
  //         stats.audioLevel = statsSubdict.audioLevel;
  //         break;
  //       case 'outbound-rtp':
  //         stats.bytesSent = statsSubdict.bytesSent;
  //         stats.voiceActivityFlag = statsSubdict.voiceActivityFlag;
  //         break;
  //       case 'remote-inbound-rtp':
  //       case 'inbound-rtp':
  //         stats.bytesReceived = statsSubdict.bytesReceived;
  //         stats.jitter = statsSubdict.jitter;
  //         stats.voiceActivityFlag = statsSubdict.voiceActivityFlag;
  //         break;
  //       case 'codec':
  //         stats.mimeType = statsSubdict.mimeType;
  //     }
  //   });
  //   return stats;
  // };

  /**
   * Add step-dependent statistics (stats that need to be computed against an interval or frequency).
   * @param prevStats
   * @param stats
   * @param frequency
   * @param stepCount
   * @returns {{audioStats, advancedStats, videoStats}}
   */
  function getComputedStats(prevStats, stats, frequency, stepCount) {
    const computedStats = {
      ...stats,
      videoStats: {
        ...stats.videoStats,
        kiloBytesSentRate: null,
        kiloBytesReceivedRate: null,
        frameRate: null,
      },
      // audioStats: {
      //   ...stats.audioStats,
      //   kiloBytesRate: null,
      // },
    };

    if (prevStats?.videoStats?.bytesSent != null && stats.videoStats?.bytesSent != null) {
      const bytesRate = (stats.videoStats.bytesSent - prevStats.videoStats.bytesSent) * frequency;
      computedStats.videoStats.kiloBytesSentRate = bytesRate / 1000;
      computedStats.videoStats.kiloBytesSentRateAvg = (((prevStats.videoStats?.kiloBytesSentRateAvg ?? 0) * (stepCount - 1)) + (computedStats.videoStats.kiloBytesSentRate ?? 0)) / (stepCount);
    } else if (prevStats?.videoStats?.kiloBytesSentRate != null) {
      computedStats.videoStats.kiloBytesSentRate = prevStats.videoStats.kiloBytesSentRate;
      computedStats.videoStats.kiloBytesSentRateAvg = prevStats.videoStats.kiloBytesSentRateAvg;
    }
    if (prevStats?.videoStats?.bytesReceived != null && stats.videoStats?.bytesReceived != null) {
      const bytesRate = (stats.videoStats.bytesReceived - prevStats.videoStats.bytesReceived) * frequency;
      computedStats.videoStats.kiloBytesReceivedRate = bytesRate / 1000;
      computedStats.videoStats.kiloBytesReceivedRateAvg = (((prevStats.videoStats?.kiloBytesReceivedRateAvg ?? 0) * (stepCount - 1)) + (computedStats.videoStats.kiloBytesReceivedRate ?? 0)) / (stepCount);
    } else if (prevStats?.videoStats?.kiloBytesReceivedRate != null) {
      computedStats.videoStats.kiloBytesReceivedRate = prevStats.videoStats.kiloBytesReceivedRate;
      computedStats.videoStats.kiloBytesReceivedRateAvg = prevStats.videoStats.kiloBytesReceivedRateAvg;
    }
    if (prevStats?.videoStats?.framesDecoded != null && stats.videoStats?.framesDecoded != null) {
      computedStats.videoStats.frameRate = (stats.videoStats.framesDecoded - prevStats.videoStats.framesDecoded) * frequency;
    } else if (prevStats?.videoStats?.frameRate != null) {
      computedStats.videoStats.frameRate = prevStats.videoStats.frameRate;
    }
    if (stats.videoStats?.latencyMs != null) {
      const newLatencyMsAvg = (((prevStats.videoStats?.latencyMsAvg ?? 0) * stepCount) + stats.videoStats.latencyMs) / (stepCount + 1);
      computedStats.videoStats.latencyMsAvg = isNaN(newLatencyMsAvg) ? computedStats.videoStats.latencyMsAvg : newLatencyMsAvg;
    } else if (prevStats?.videoStats?.latencyMsAvg != null) {
      computedStats.videoStats.latencyMsAvg = prevStats?.videoStats?.latencyMsAvg;
    }

    // if (prevStats?.audioStats?.bytesSent != null && stats.audioStats?.bytesSent != null) {
    //   const bytesRate = (stats.audioStats.bytesSent - prevStats.audioStats.bytesSent) * frequency;
    //   computedStats.audioStats.kiloBytesRate = bytesRate / 1000;
    // }
    // if (prevStats?.audioStats?.bytesReceived != null && stats.audioStats?.bytesReceived != null) {
    //   const bytesRate = (stats.audioStats.bytesReceived - prevStats.audioStats.bytesReceived) * frequency;
    //   computedStats.audioStats.kiloBytesRate = bytesRate / 1000;
    // }
    return computedStats;
  }

  if (stream instanceof MediaStream) {
    start(stream, { logger, onStats, interval });
  }

  return {
    start,
    stop,
    instant,
  };
};
